/**
 * A 6-digit authenticator-app code field with its submit button and error.
 */

import { useState, type FormEvent } from 'react';
import { totpErrorMessage } from '@shared/auth/totpErrorMessage';
import {
  AUTH_ERROR_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_SUBMIT_CLASS,
  AUTH_SUBMIT_STYLE,
} from './authFormStyles';

const CODE_LENGTH = 6;

interface TotpCodeFormProps {
  submitLabel: string;
  /** Resolves when the code is accepted; rejects with the verification error. */
  onSubmit: (code: string) => Promise<void>;
}

export function TotpCodeForm({ submitLabel, onSubmit }: TotpCodeFormProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await onSubmit(code);
    } catch (err: unknown) {
      setError(totpErrorMessage(err));
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="mb-4">
        <label htmlFor="totp-code" className={AUTH_LABEL_CLASS}>
          Authentication code
        </label>
        <input
          id="totp-code"
          name="totp-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={CODE_LENGTH}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
          placeholder="123456"
          className={`${AUTH_INPUT_CLASS} text-center tracking-[0.4em]`}
        />
      </div>

      {error && (
        <div role="alert" className={AUTH_ERROR_CLASS}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting || code.length !== CODE_LENGTH}
        className={AUTH_SUBMIT_CLASS}
        style={AUTH_SUBMIT_STYLE}
      >
        {isSubmitting ? 'Verifying...' : submitLabel}
      </button>
    </form>
  );
}
